import { LEVELS } from '../constants/levels.js';
import { todayStr, getWeekDates, getPace } from './utils.js';

export function buildEmailHTML(title, body, ctaText, ctaUrl) {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><style>
    body{font-family:'Nunito',Helvetica,Arial,sans-serif;margin:0;padding:0;background:#F4F5F7;}
    .wrap{max-width:600px;margin:0 auto;background:#fff;border-radius:12px;overflow:hidden;}
    .header{background:#061928;padding:24px;text-align:center;}
    .header h1{font-family:'Fredoka',sans-serif;color:#00B4D8;margin:0;font-size:24px;}
    .header .dolphin{font-size:36px;}
    .body{padding:24px 28px;}
    .body h2{font-family:'Fredoka',sans-serif;color:#061928;font-size:20px;margin:0 0 12px;}
    .body p{color:#333;font-size:16px;line-height:1.6;margin:0 0 16px;}
    .tip{background:#F0F9FF;border-left:4px solid #00B4D8;padding:16px;border-radius:0 8px 8px 0;margin:20px 0;}
    .tip strong{color:#061928;}
    .cta{display:inline-block;background:#FF6B35;color:#fff;padding:14px 28px;border-radius:28px;text-decoration:none;font-weight:700;font-size:16px;margin:8px 0;}
    .footer{background:#F4F5F7;padding:20px 28px;text-align:center;font-size:12px;color:#888;}
    .footer a{color:#00B4D8;}
  </style></head><body>
    <div class="wrap">
      <div class="header"><span class="dolphin">${'\u{1F42C}'}</span><h1>Finley</h1></div>
      <div class="body"><h2>${title}</h2>${body}
        ${ctaText ? '<p style="text-align:center"><a class="cta" href="' + ctaUrl + '">' + ctaText + '</a></p>' : ''}
      </div>
      <div class="footer">
        <p>Built on the Science of Reading</p>
        <p><a href="https://finleyreads.com/privacy">Privacy</a> · <a href="https://finleyreads.com/terms">Terms</a> · <a href="https://finleyreads.com">finleyreads.com</a></p>
        <p>You're receiving this because you signed up for Finley. <a href="#">Unsubscribe</a></p>
      </div>
    </div>
  </body></html>`;
}

export function getWelcomeEmail(childName) {
  const n = escHtml(childName);
  return {
    subject: childName + "'s reading journey starts now",
    html: buildEmailHTML(
      "Welcome to Finley!",
      '<p>You just did something incredible for ' + n + '. By starting Finley, you\'re giving them access to a personal AI reading tutor that adapts to their exact level — every single session.</p>' +
      '<div class="tip"><strong>Parent Tip:</strong> The single most powerful thing you can do for your child\'s reading? Read WITH them for 15 minutes a day. Not instead of Finley — alongside it. When you read together, your child hears fluent reading, learns new vocabulary from context, and associates reading with quality time with you. That combination — your voice + Finley\'s adaptive practice — is how reading confidence is built.</div>' +
      '<p>Here\'s what to expect in the first week:</p>' +
      '<p>• <strong>Day 1-2:</strong> Finley is learning ' + n + '\'s level. Content may feel easy — that\'s intentional. Confidence first.</p>' +
      '<p>• <strong>Day 3-5:</strong> You\'ll notice the content adjusting. Finley is targeting the exact skills ' + n + ' needs most.</p>' +
      '<p>• <strong>Day 7:</strong> Check the parent dashboard — you\'ll see real data on what was practiced and mastered.</p>',
      "Open Finley",
      "https://finleyreads.com/app"
    )
  };
}

export function getDay3Email(childName, wordsLearned, minutesPracticed) {
  const n = escHtml(childName);
  return {
    subject: childName + " is making progress — here's what to do next",
    html: buildEmailHTML(
      "3 Days In — Here's What's Happening",
      '<p>' + n + ' has practiced for <strong>' + minutesPracticed + ' minutes</strong> and worked on <strong>' + wordsLearned + ' words</strong> so far. Every minute counts.</p>' +
      '<div class="tip"><strong>Tonight\'s Activity:</strong> Pick any book ' + n + ' enjoys. Before you read a page, point to 2-3 words and ask "Do you know this word?" If they do — celebrate it. If they don\'t — read the sentence together, then come back to the word. This simple technique is called "word consciousness" and it\'s one of the most effective vocabulary builders research has found. Finley does this digitally — you can reinforce it at home with any book.</div>' +
      '<p><strong>What Finley\'s adaptive engine is doing:</strong> Behind the scenes, Finley is tracking mastery across 40+ individual reading skills using Bayesian Knowledge Tracing. When ' + n + ' gets something right, Finley knows if it was real understanding or a lucky guess (based on response time). When they struggle, Finley adjusts immediately — not after a test, not after a week. Every. Single. Response.</p>' +
      '<p>Keep going. Consistency beats intensity. 10-15 minutes a day builds more than an hour once a week.</p>',
      "See " + n + "'s Progress",
      "https://finleyreads.com/app"
    )
  };
}

export function getDay7Email(childName, level, wordsLearned, minutesPracticed) {
  const n = escHtml(childName);
  const lvInfo = LEVELS[level] || LEVELS[3];
  return {
    subject: "Week 1 complete — " + childName + "'s reading report",
    html: buildEmailHTML(
      "One Week of Growth",
      '<p>Here\'s ' + n + '\'s first weekly snapshot:</p>' +
      '<p>• <strong>Finley Reading Level:</strong> ' + level + ' (' + escHtml(lvInfo.name) + ' · ' + escHtml(lvInfo.grade) + ')</p>' +
      '<p>• <strong>Total Practice Time:</strong> ' + minutesPracticed + ' minutes</p>' +
      '<p>• <strong>Words Practiced:</strong> ' + wordsLearned + '</p>' +
      '<p>These numbers matter because they represent real adaptive practice — not passive screen time. Every word ' + n + ' practiced was selected specifically for their skill gaps by the BKT engine.</p>' +
      '<div class="tip"><strong>This Week\'s Strategy — "The 5 Finger Rule":</strong> When ' + n + ' picks a book to read independently, have them read one page and hold up a finger for each word they don\'t know. 0-1 fingers = too easy (still fun for confidence!). 2-3 fingers = just right. 4-5 fingers = save it for later or read it together. This teaches kids to self-select appropriate books — the same skill Finley\'s adaptive engine uses automatically.</div>' +
      '<p>Want to see the full breakdown? Open the parent dashboard in Finley — it shows skill-by-skill mastery, words mastered vs. in progress, and daily session details.</p>',
      "View Full Dashboard",
      "https://finleyreads.com/app"
    )
  };
}

export async function sendEmail(to, emailData) {
  try {
    const res = await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to, subject: emailData.subject, html: emailData.html }),
    });
    return await res.json();
  } catch (e) {
    console.warn('Email send failed:', e);
    return { error: e.message };
  }
}

export function escHtml(s) {
  return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

export function getParentEmail() {
  try { return localStorage.getItem('rq:parentEmail') || ''; } catch { return ''; }
}

export function saveParentEmail(email) {
  try { localStorage.setItem('rq:parentEmail', email); } catch {}
}

export function getEmailsSent(childId) {
  try { const v = localStorage.getItem('rq:emailsSent:' + childId); return v ? JSON.parse(v) : {}; } catch { return {}; }
}

export function markEmailSent(childId, type) {
  try { const sent = getEmailsSent(childId); sent[type] = todayStr(); localStorage.setItem('rq:emailsSent:' + childId, JSON.stringify(sent)); } catch {}
}

export function getWeeklyEmailKey(childId) {
  const now = new Date(); const day = now.getDay(); const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(now.setDate(diff)); const week = monday.toISOString().slice(0, 10);
  return 'rq:weeklyEmail:' + childId + ':' + week;
}

export async function checkAndSendEmailTriggers(profiles) {
  const email = getParentEmail();
  if (!email) return;
  const now = new Date();
  for (const p of profiles) {
    const sent = getEmailsSent(p.id);
    const created = p.tracker?.log?.[0]?.t;
    if (!created) continue;
    const daysSince = Math.floor((now.getTime() - created) / 86400000);
    if (daysSince >= 3 && daysSince <= 10 && !sent.day3) {
      const totalItems = p.tracker.log?.length || 0;
      const minutes = Math.round(totalItems * 0.3);
      try {
        const result = await sendEmail(email, getDay3Email(p.name, totalItems, minutes));
        if (!result.error) markEmailSent(p.id, 'day3');
      } catch {}
    }
    if (daysSince >= 7 && daysSince <= 14 && !sent.day7) {
      const totalItems = p.tracker.log?.length || 0;
      const minutes = Math.round(totalItems * 0.3);
      const lv = p.tracker.currentLevel;
      try {
        const result = await sendEmail(email, getDay7Email(p.name, lv, totalItems, minutes));
        if (!result.error) markEmailSent(p.id, 'day7');
      } catch {}
    }
    if (now.getDay() === 1) {
      const weekKey = getWeeklyEmailKey(p.id);
      try {
        if (!localStorage.getItem(weekKey)) {
          const logHtml = buildReadingLogHTML(p.name, p.age, p.tracker, p.id, -1);
          const result = await sendEmail(email, { subject: p.name + "'s Weekly Reading Log", html: logHtml });
          if (!result.error) localStorage.setItem(weekKey, todayStr());
        }
      } catch {}
    }
  }
}

export async function checkAndSendWelcomeEmail(profile) {
  const email = getParentEmail();
  if (!email) return;
  const sent = getEmailsSent(profile.id);
  if (sent.welcome) return;
  try {
    const result = await sendEmail(email, getWelcomeEmail(profile.name));
    if (!result.error) markEmailSent(profile.id, 'welcome');
  } catch {}
}

export function buildReadingLogHTML(name, age, tracker, childId, weekOffset) {
  const days = getWeekDates(weekOffset || 0);
  const log = tracker.log || [];
  const dayRows = days.map(d => {
    const ds = d.toISOString().slice(0, 10);
    const dayLog = log.filter(e => new Date(e.t).toISOString().slice(0, 10) === ds);
    const mins = Math.round(dayLog.length * 0.3);
    const correct = dayLog.filter(e => e.c).length;
    const total = dayLog.length;
    const dayName = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    return '<tr><td style="padding:8px 12px;border-bottom:1px solid #F0F0F5;">' + dayName + '</td><td style="padding:8px 12px;border-bottom:1px solid #F0F0F5;">' + mins + ' min</td><td style="padding:8px 12px;border-bottom:1px solid #F0F0F5;">' + correct + '/' + total + '</td></tr>';
  }).join('');
  const lv = LEVELS[tracker.currentLevel] || LEVELS[3];
  return buildEmailHTML(
    name + "'s Weekly Reading Log",
    '<p><strong>Level:</strong> ' + tracker.currentLevel + ' (' + lv.name + ' · ' + lv.grade + ') · <strong>Age:</strong> ' + age + '</p>' +
    '<table style="width:100%;border-collapse:collapse;margin:16px 0;"><thead><tr style="background:#F4F5F7;"><th style="padding:8px 12px;text-align:left;font-size:13px;">Day</th><th style="padding:8px 12px;text-align:left;font-size:13px;">Time</th><th style="padding:8px 12px;text-align:left;font-size:13px;">Accuracy</th></tr></thead><tbody>' + dayRows + '</tbody></table>' +
    '<p style="font-size:13px;color:#666;">Generated by Finley · ' + new Date().toLocaleDateString() + '</p>',
    "Open Dashboard",
    "https://finleyreads.com/app"
  );
}

export function buildReportCardHTML(name, age, tracker, childId) {
  const lv = LEVELS[tracker.currentLevel] || LEVELS[3];
  const modes = ['phonics', 'sight', 'stories', 'bubbles'];
  const modeRows = modes.map(m => {
    const s = tracker.ms[m] || { c: 0, t: 0 };
    const pct = s.t > 0 ? Math.round((s.c / s.t) * 100) : 0;
    return '<tr><td style="padding:8px 12px;border-bottom:1px solid #F0F0F5;text-transform:capitalize;">' + m + '</td><td style="padding:8px 12px;border-bottom:1px solid #F0F0F5;">' + s.c + '/' + s.t + '</td><td style="padding:8px 12px;border-bottom:1px solid #F0F0F5;">' + pct + '%</td></tr>';
  }).join('');
  const pace = getPace(tracker.currentLevel, age);
  return buildEmailHTML(
    name + "'s Progress Report",
    '<p><strong>Reading Level:</strong> ' + tracker.currentLevel + ' (' + lv.name + ' · ' + lv.grade + ')</p>' +
    '<p><strong>Status:</strong> ' + pace.label + '</p>' +
    '<p><strong>Total Rounds:</strong> ' + tracker.rounds + ' · <strong>Age:</strong> ' + age + '</p>' +
    '<table style="width:100%;border-collapse:collapse;margin:16px 0;"><thead><tr style="background:#F4F5F7;"><th style="padding:8px 12px;text-align:left;font-size:13px;">Mode</th><th style="padding:8px 12px;text-align:left;font-size:13px;">Correct</th><th style="padding:8px 12px;text-align:left;font-size:13px;">Accuracy</th></tr></thead><tbody>' + modeRows + '</tbody></table>' +
    (tracker.weakWords && tracker.weakWords.length > 0 ? '<p><strong>Words to practice:</strong> ' + tracker.weakWords.slice(0, 10).join(', ') + '</p>' : '') +
    '<p style="font-size:13px;color:#666;">Generated by Finley · ' + new Date().toLocaleDateString() + '</p>',
    "View Full Dashboard",
    "https://finleyreads.com/app"
  );
}
